import { getIdTokenSync } from "./oidcAuth";

export type ApiEndpoint = {
  path: string;
  methods: string[];
};

export type CorsDiagnosticResult = {
  success: boolean;
  path: string;
  materializedPath: string;
  method: string;
  origin: string;
  isPreflightRequest: boolean;
  isOriginAllowed: boolean;
  methodAllowed: boolean;
  requiredHeadersAllowed: boolean;
  allowedMethods: string[];
  allowedHeaders: string[];
  allowedOrigins: string[];
  supportsCredentials: boolean;
  message: string;
};

export type GatewayEndpointProbe = {
  success: boolean;
  status: number;
  path: string;
  materializedPath: string;
  method: string;
  expectedOrigin: string;
  actualOrigin: string | null;
  originMatches: boolean;
  expectedCredentials: string;
  actualCredentials: string | null;
  credentialsMatch: boolean;
  allowedMethods: string[];
  methodAllowed: boolean;
  allowedHeaders: string[];
  requiredHeadersAllowed: boolean;
  responseHeaders: Record<string, string>;
  message: string;
};

type EndpointsResponse = {
  data: ApiEndpoint[];
};

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api";
const DEVELOPMENT_BASE = "/development";
const requestHeaders = (): HeadersInit => {
  const idToken = getIdTokenSync();
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
};

export const loadApiEndpoints = async (signal?: AbortSignal): Promise<ApiEndpoint[]> => {
  const response = await fetch(`${API_BASE_URL}${DEVELOPMENT_BASE}/endpoints`, {
    signal,
    cache: "no-store",
    headers: requestHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Não foi possível carregar o catálogo de diagnóstico (HTTP ${response.status}).`);
  }

  const body = await response.json() as EndpointsResponse;
  return body.data;
};

export const evaluateCors = async (
  path: string,
  method: string,
  origin: string,
): Promise<CorsDiagnosticResult> => {
  const query = new URLSearchParams({ path, method, origin });
  const response = await fetch(`${API_BASE_URL}${DEVELOPMENT_BASE}/cors-diagnostics?${query}`, {
    cache: "no-store",
    headers: requestHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Falha ao avaliar a policy CORS (HTTP ${response.status}).`);
  }

  return response.json() as Promise<CorsDiagnosticResult>;
};

export const probeGatewayEndpoint = async (
  path: string,
  method: string,
): Promise<GatewayEndpointProbe> => {
  const query = new URLSearchParams({
    path,
    method: method.toUpperCase(),
    origin: window.location.origin,
    gatewayBaseUrl: API_BASE_URL.replace(/\/api\/?$/, ""),
  });
  const response = await fetch(`${API_BASE_URL}${DEVELOPMENT_BASE}/gateway-probe?${query}`, {
    cache: "no-store",
    headers: requestHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Falha ao consultar o Gateway pelo diagnóstico (HTTP ${response.status}).`);
  }

  return response.json() as Promise<GatewayEndpointProbe>;
};
